import React, { useCallback, useMemo } from 'react';
import TagsInput from 'react-tagsinput-special';
import { Button, NavDropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimesCircle,
  faPlus,
  faCaretDown,
  faBan,
  faSync,
  faExclamationCircle,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import classNames from 'classnames/bind';
import _ from 'lodash';
import 'react-tagsinput/react-tagsinput.css';
import styles from './tags.module.scss';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import i18n from 'i18n';
import { CopyableOverlayTrigger } from 'components/CopyableOverlayTrigger/CopyableOverlayTrigger';
import {
  SpaceHealthState,
  SPACE_HEALTH_SEVERITY,
  parseSpaceHealthState
} from 'enum/SpaceHealthState';

const cx = classNames.bind(styles);

// ECOPD-5255: per-state chip class + leading icon (Tabler icons mapped to
// the closest FontAwesome equivalents). Only applied when a tag carries a
// `healthState`, so chips without it keep their existing appearance.
//
// ECOPD-5655: both maps are exported so the device-management status chip
// shows the same variant and the same icon as the campaign space picker.
// The matching CSS rules in tags.module.scss are compound selectors nested
// under the tags input (`.react-tagsinput-tag.healthBanned`), so they only
// apply inside a Tags container — a caller outside one reuses the variant
// name as a key into its own stylesheet.
export const HEALTH_STATE_CLASS: Record<SpaceHealthState, string> = {
  [SpaceHealthState.BANNED]: 'healthBanned',
  [SpaceHealthState.WARMING_UP]: 'healthWarmingUp',
  [SpaceHealthState.NO_DATA]: 'healthNoData',
  [SpaceHealthState.NORMAL]: 'healthNormal'
};

export const HEALTH_STATE_ICON: Record<
  SpaceHealthState,
  IconDefinition | undefined
> = {
  [SpaceHealthState.BANNED]: faBan, // ti-screen-share-off
  [SpaceHealthState.WARMING_UP]: faSync, // ti-refresh
  [SpaceHealthState.NO_DATA]: faExclamationCircle, // ti-circle-off
  [SpaceHealthState.NORMAL]: undefined
};

// Only outdoor chips carry `healthState` (set via the outdoor form model's
// healthGetter); every other Tags usage returns undefined here and is unaffected.
function getTagHealthState(tag): SpaceHealthState | undefined {
  return parseSpaceHealthState(_.get(tag, 'healthState'));
}

// 已刪除蓋過運作狀態的外觀：版位都刪了，「正常」或「停止服務」都已經不是重點。
function isTagDeleted(tag): boolean {
  return _.get(tag, 'healthDeleted') === true;
}

function defaultRenderTag(
  disableReason,
  tipsConfig:
    | {
        debounceTrigger?: number;
        renderTips:
          | ((limitation: SelectOptions) => React.ReactNode | undefined)
          | undefined;
      }
    | undefined,
  suggestions: SelectOptions[] | undefined,
  props
) {
  const {
    tag,
    key,
    onRemove,
    classNameRemove,
    getTagDisplayValue,
    disabled,
    className,
    ...other
  } = props;

  const clickCallback = () => {
    onRemove(key);
  };

  const tagValue = typeof tag === 'string' ? tag : _.get(tag, 'value', '');
  const tagOption = suggestions
    ? suggestions.find(
        (suggestion) => suggestion.value.toString() === tagValue.toString()
      )
    : undefined;
  const tagLabel = tagOption ? tagOption.label : _.get(tag, 'label', tagValue);
  const tagIsError =
    _.get(tag, 'isError', false) ||
    (suggestions && suggestions.length > 0 ? !tagOption : false);
  const deleted = isTagDeleted(tag);
  const healthState = getTagHealthState(tag);
  const healthClass = deleted
    ? 'spaceDeleted'
    : healthState
      ? HEALTH_STATE_CLASS[healthState]
      : undefined;
  const healthIcon = deleted
    ? faTrash
    : healthState
      ? HEALTH_STATE_ICON[healthState]
      : undefined;
  const tagClassName = cx(className, {
    errorTag: tagIsError,
    disabled,
    [healthClass as string]: !!healthClass
  });
  const removeButtonClass = cx(classNameRemove, {
    disabled: disabled
  });

  return (
    <CopyableOverlayTrigger
      key={key}
      placement='bottom'
      minWidth={130}
      maxWidth={390}
      maxHeight={260}
      debounceTrigger={tipsConfig?.debounceTrigger}
      overlay={tipsConfig?.renderTips ? tipsConfig.renderTips(tag) : undefined}
    >
      <span className={tagClassName} {...other}>
        {healthIcon && (
          <FontAwesomeIcon className={styles.healthIcon} icon={healthIcon} />
        )}
        {deleted ? (
          <span className={cx('deletedLabel')}>
            {getTagDisplayValue(tagLabel)}
          </span>
        ) : (
          getTagDisplayValue(tagLabel)
        )}
        {disabled && disableReason ? (
          <IconWithTooltip
            className={removeButtonClass}
            icon={faTimesCircle}
            tooltipProps={{
              id: `tagDisableTip-${key}`,
              tooltip: i18n.t(disableReason)
            }}
          />
        ) : (
          <a
            className={removeButtonClass}
            onClick={disabled ? undefined : clickCallback}
          >
            <FontAwesomeIcon icon={faTimesCircle} />
          </a>
        )}
      </span>
    </CopyableOverlayTrigger>
  );
}

function defaultRenderInput(props) {
  const { onChange, value } = props;
  const other = _.omit(props, ['onChange', 'value', 'addTag']);
  return <input type='text' onChange={onChange} value={value} {...other} />;
}

function emptyInput(_props) {
  return null;
}

type TagsProps = {
  value: string[];
  onChange: (tags: string[]) => void;
  disableInput?: boolean;
  operate?: any;
  addAction?: any;
  showPlus?: boolean;
  disabled?: boolean;
  disableReason?: string;
  addOnBlur?: boolean;
  placeholder?: string;
  suggestions?: SelectOptions[];
  tipsConfig?: {
    debounceTrigger?: number;
    renderTips: (limitation: SelectOptions) => React.ReactNode | undefined;
  };
};

const Tags: React.FC<TagsProps & React.HTMLAttributes<HTMLDivElement>> = ({
  value = [],
  onChange: handleChange = _.noop,
  operate = '',
  addOnBlur = true,
  addAction = _.noop,
  showPlus = false,
  placeholder,
  className: parentClassName,
  disableInput = false,
  disabled = false,
  disableReason,
  suggestions = [],
  tipsConfig
}) => {
  const inputClass = cx({
    'react-tagsinput': true,
    [operate]: true,
    disabled
  });
  const containerClass = cx(parentClassName, {
    tagContainer: true,
    hasSuggestions: suggestions && suggestions.length > 0
  });
  const needShowPlus = showPlus && !disabled;
  const buttonClass = cx('limitaionPlus', {
    showPlus: needShowPlus,
    noshowPlus: !needShowPlus
  });
  const renderInput = disableInput ? emptyInput : defaultRenderInput;
  const clickFun = (e) => {
    if (
      typeof e.target.className !== 'object' &&
      e.target.className !== 'react-tagsinput-tag'
    ) {
      addAction(e);
    }
  };

  const safeValue = useMemo(() => {
    const arr = Array.isArray(value) ? value : [];
    // Sort priority: error chips first, then deleted chips, then abnormal-health
    // chips by severity (banned/no_data float to the front per ECOPD-5255), then the rest.
    const healthRank = (tag) => {
      if (isTagDeleted(tag)) return 0;
      const state = getTagHealthState(tag);
      return state ? SPACE_HEALTH_SEVERITY[state] : Number.POSITIVE_INFINITY;
    };
    return [...arr].sort((a, b) => {
      const aIsError = _.get(a, 'isError', false) ? 1 : 0;
      const bIsError = _.get(b, 'isError', false) ? 1 : 0;
      if (aIsError !== bIsError) return bIsError - aIsError;
      const ra = healthRank(a);
      const rb = healthRank(b);
      // Equal (incl. both Infinity → NaN otherwise) keeps stable order.
      return ra === rb ? 0 : ra - rb;
    });
  }, [value]);
  const [inputValue, setInputValue] = React.useState('');
  const [showSuggestions, setShowSuggestions] = React.useState(false);

  const isSuggestionAlreadySelected = useCallback(
    (suggestion: SelectOptions) => {
      return safeValue.some((tag: string) => {
        return tag === suggestion.value.toString();
      });
    },
    [safeValue]
  );

  const filteredSuggestions = useMemo(() => {
    if (!suggestions) {
      return [];
    }
    return suggestions.filter((suggestion) => {
      const isSelected = isSuggestionAlreadySelected(suggestion);
      if (isSelected) {
        return false;
      }
      return (
        suggestion.label.toLowerCase().includes(inputValue.toLowerCase()) ||
        suggestion.value
          .toString()
          .toLowerCase()
          .includes(inputValue.toLowerCase())
      );
    });
  }, [inputValue, suggestions, isSuggestionAlreadySelected]);

  const renderSuggestions = useCallback(() => {
    return filteredSuggestions.map((suggestion) => {
      // onMouseDown's default event is blur event,
      // invoke preventDefault to prevent onBlur event to be triggered
      const onMouseDown = (e) => {
        e.stopPropagation();
        e.preventDefault();
        const newValue = _.uniq([...safeValue, suggestion.value.toString()]);
        handleChange(newValue);
        setInputValue('');
        setShowSuggestions(false);
      };
      return (
        <NavDropdown.Item key={suggestion.value} onMouseDown={onMouseDown}>
          {suggestion.label}
        </NavDropdown.Item>
      );
    });
  }, [safeValue, filteredSuggestions, handleChange]);

  const onChangeInput = useCallback((input) => {
    setShowSuggestions(true);
    setInputValue(input);
  }, []);

  const triggerSuggestionMenu = useCallback((e) => {
    if (e.target.className === 'react-tagsinput-input') {
      setShowSuggestions((prev) => !prev);
    }
  }, []);

  const closeSuggestionsMenu = useCallback(() => {
    if (!_.isEmpty(inputValue)) {
      const newValue = _.uniq([...safeValue, inputValue.toString()]);
      handleChange(newValue);
      setInputValue('');
    }
    setShowSuggestions(false);
  }, [inputValue, safeValue, handleChange]);

  return (
    <div
      className={containerClass}
      onClick={disabled ? undefined : clickFun}
      onMouseDown={triggerSuggestionMenu}
      onBlur={closeSuggestionsMenu}
    >
      <TagsInput
        value={safeValue}
        onChange={handleChange}
        renderTag={_.partial(
          defaultRenderTag,
          disableReason,
          tipsConfig,
          suggestions
        )}
        className={inputClass}
        inputProps={{ placeholder }}
        renderInput={renderInput}
        addOnBlur={addOnBlur}
        disabled={disabled}
        inputValue={inputValue}
        onChangeInput={onChangeInput}
      />
      <Button variant='link' className={buttonClass} onClick={addAction}>
        <FontAwesomeIcon icon={faPlus} />
      </Button>
      {filteredSuggestions.length > 0 && (
        <>
          <NavDropdown id='suggestions' title='' show={showSuggestions}>
            {renderSuggestions()}
          </NavDropdown>
          <FontAwesomeIcon icon={faCaretDown} className={styles.caretDown} />
        </>
      )}
    </div>
  );
};

export default Tags;
